import { h } from 'preact';
import { useState } from 'preact/hooks';
import classNames from 'classnames';
import { UrlUnfurledData } from 'core_main/pubnub/types';
import {
  URL_UNFURLER_UTM_SOURCE,
  URL_UNFURLER_UTM_PARAM,
} from 'widget_main/globals/constants';
import styles from './urlPreview.scss';

interface UrlPreviewProps {
  preview: UrlUnfurledData;
  /** Original message direction ('in' for inbound from agent, 'out' for outbound from customer) */
  direction: 'in' | 'out';
}

/**
 * Decode HTML entities in a string
 *
 * Uses browser's built-in HTML parsing to decode entities like &amp;, &lt;, &gt;, &quot;, &#39;, etc.
 * This is safe for text content that should be displayed as plain text (not HTML).
 *
 * @param html - String containing HTML entities to decode
 * @returns Decoded string with HTML entities converted to their character equivalents
 *
 * @example
 * ```typescript
 * decodeHtmlEntities('Hello &amp; World') // Returns: 'Hello & World'
 * decodeHtmlEntities('Price: &pound;10') // Returns: 'Price: £10'
 * decodeHtmlEntities('&lt;script&gt;') // Returns: '<script>'
 * ```
 */
const decodeHtmlEntities = (html: string): string => {
  if (!html) {
    return html;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

/**
 * Add UTM source parameter to URL for tracking purposes
 *
 * Adds the `utm_source` parameter with value from `URL_UNFURLER_UTM_SOURCE` constant
 * to track clicks from URL previews. Uses proper URL parsing when possible, with
 * fallback to string manipulation for invalid URLs.
 *
 * @param url - The original URL to add UTM parameter to
 * @returns URL with `utm_source` parameter added
 *   - Adds `?utm_source=...` if URL has no query parameters
 *   - Adds `&utm_source=...` if URL already has query parameters
 *
 * @example
 * ```typescript
 * addUtmSourceToUrl('https://example.com')
 * // Returns: 'https://example.com?utm_source=kustomer_url_preview'
 *
 * addUtmSourceToUrl('https://example.com?foo=bar')
 * // Returns: 'https://example.com?foo=bar&utm_source=kustomer_url_preview'
 * ```
 */
const addUtmSourceToUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set(URL_UNFURLER_UTM_PARAM, URL_UNFURLER_UTM_SOURCE);
    return urlObj.toString();
  } catch {
    // If URL parsing fails, fallback to string manipulation
    const hasQuery = url.includes('?');
    const separator = hasQuery ? '&' : '?';
    return `${url}${separator}${URL_UNFURLER_UTM_PARAM}=${URL_UNFURLER_UTM_SOURCE}`;
  }
};

/**
 * Convert HTTP image URLs to HTTPS for secure loading
 *
 * Replaces `http://` with `https://` in image URLs to ensure secure image loading.
 * This helps prevent mixed content warnings and ensures images load over HTTPS.
 *
 * @param imageUrl - The original image URL (may be HTTP or HTTPS)
 * @returns Image URL with HTTPS protocol, or original URL if already HTTPS or invalid
 *
 * @example
 * ```typescript
 * convertImageUrlToHttps('http://example.com/image.jpg')
 * // Returns: 'https://example.com/image.jpg'
 *
 * convertImageUrlToHttps('https://example.com/image.jpg')
 * // Returns: 'https://example.com/image.jpg'
 * ```
 */
const convertImageUrlToHttps = (imageUrl: string): string => {
  if (!imageUrl) {
    return imageUrl;
  }
  // Replace http:// with https:// for secure image loading
  return imageUrl.replace(/^http:\/\//i, 'https://');
};

/**
 * UrlPreview Component
 *
 * Renders a single URL preview card with image, title, description, and URL.
 * The preview is clickable and opens the URL in a new tab with UTM tracking.
 * Only HTTPS URLs are allowed - non-HTTPS URLs are disabled and cannot be opened.
 *
 * Features:
 * - Displays preview image (if available) with proper aspect ratio
 * - Automatically converts HTTP image URLs to HTTPS for secure loading
 * - Shows title, description, and URL
 * - Handles image load errors gracefully
 * - Adds UTM tracking parameter to all URLs
 * - Opens URLs in new tab with security attributes
 * - Validates URL protocol - only HTTPS URLs are clickable
 * - Accessible with proper ARIA labels
 * - Uses original message direction for styling consistency
 *
 * @param preview - URL unfurled data containing url, title, description, image
 * @param direction - Original message direction ('in' for inbound from agent, 'out' for outbound from customer)
 *
 * @example
 * ```tsx
 * <UrlPreview
 *   preview={{
 *     url: 'https://example.com',
 *     title: 'Example Site',
 *     description: 'An example website',
 *     image: 'https://example.com/image.jpg'
 *   }}
 *   direction="out"
 * />
 * ```
 */
const UrlPreview = ({ preview, direction }: UrlPreviewProps) => {
  const { url, title, description, image } = preview;
  const [imageError, setImageError] = useState(false);
  
  // Validate URL protocol - only allow https
  const isValidHttpsUrl = (() => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'https:';
    } catch {
      // If URL parsing fails, check if it starts with https://
      return url.startsWith('https://');
    }
  })();

  const urlWithUtm = isValidHttpsUrl ? addUtmSourceToUrl(url) : '#';

  // Decode HTML entities in title and description for proper display
  const decodedTitle = title ? decodeHtmlEntities(title) : undefined;
  const decodedDescription = description ? decodeHtmlEntities(description) : undefined;

  // Convert HTTP image URLs to HTTPS for secure loading
  const secureImageUrl = image ? convertImageUrlToHttps(image) : undefined;

  // Determine if image should be shown (exists and hasn't errored)
  const showImage = secureImageUrl && !imageError;

  const handleClick = (e: MouseEvent) => {
    // Only prevent navigation for non-HTTPS URLs
    e.preventDefault();
    e.stopPropagation();
  };

  const linkProps = isValidHttpsUrl
    ? {
        href: urlWithUtm,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {
        href: '#',
        onClick: handleClick,
        'aria-disabled': true,
      };

  return (
    <a
      {...linkProps}
      className={classNames(styles.urlPreview, {
        [styles.urlPreviewWithoutImage]: !showImage,
      })}
      data-direction={direction}
      aria-label={decodedTitle || url}
    >
      {showImage && (
        <img
          src={secureImageUrl}
          alt=""
          className={styles.urlPreviewImage}
          onError={() => setImageError(true)}
        />
      )}
      <div className={styles.urlPreviewContent}>
        <div className={styles.urlPreviewText}>
          {decodedTitle && <div className={styles.urlPreviewTitle}>{decodedTitle}</div>}
          {decodedDescription && (
            <div className={styles.urlPreviewDescription}>{decodedDescription}</div>
          )}
          <div className={styles.urlPreviewUrl}>{url}</div>
        </div>
      </div>
    </a>
  );
};

export default UrlPreview;
